import type { ConstantsBillingStruct } from "@gosaas/types";
import { useQuery } from "@tanstack/react-query";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

export function billingDetailsQueryKey(teamId: string | null, sync?: boolean) {
	return ["billing-details", teamId, sync ? "sync" : "default"] as const;
}

export async function fetchBillingDetailsData(
	teamId: string,
	sync?: boolean,
): Promise<ConstantsBillingStruct | null> {
	const response = await requestWithHandling<ConstantsBillingStruct>(() =>
		apiClient.api.billingDetailsList(
			teamId,
			sync ? { query: { sync: "true" } } : undefined,
		),
	);
	if (response.code !== "SUCCESS") {
		throw new Error(response.message || "Failed to fetch billing details");
	}
	return response.data ?? null;
}

function shouldRetryBillingQuery(
	failureCount: number,
	error: unknown,
): boolean {
	if (
		error instanceof TypedClientApiError &&
		error.code === "RATE_LIMIT_EXCEEDED"
	) {
		return false;
	}
	return failureCount < 1;
}

export function useBillingDetails(
	teamId: string | null,
	options?: { sync?: boolean; enabled?: boolean },
) {
	const resolvedTeamId = teamId;
	return useQuery({
		queryKey: billingDetailsQueryKey(resolvedTeamId, options?.sync),
		queryFn: () => {
			if (!resolvedTeamId) {
				throw new Error("Billing details query requires a team id");
			}
			return fetchBillingDetailsData(resolvedTeamId, options?.sync);
		},
		enabled: !!resolvedTeamId && (options?.enabled ?? true),
		staleTime: 30_000,
		retry: shouldRetryBillingQuery,
	});
}
